<script>
// Agent Calendar — Phase 3 (docs/real-estate/be/10-api-reference.md §A11).
// Layout and FullCalendar wiring are adapted from
// organization/staffCalendar/Index.vue, but this only ever talks to the
// Real Estate `realEstateCalendar` store — see CLAUDE.md's "copy and
// adapt, don't share" note.
//
// Click routing (see the Phase 3 plan's interaction table) is by
// `extendedProps.kind`/`source_type`:
//   booking             -> fetch the full appointment, then Edit/Reschedule
//   unavailability       -> a details popover with Delete (no edit endpoint)
//   operating_hours      -> an info popover, no edit here
//   specific_assignment  -> AssignmentModal, edit mode, fetched by id
//   schedule             -> ScheduleModal, edit mode, fetched by id
// Right-click delete only for specific_assignment/unavailability — deleting
// a schedule day would delete the whole schedule, so it isn't offered.
import { mapActions, mapGetters } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { format, parseISO } from 'date-fns';
import { getContrastingTextColor } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import RealEstateTabs from '../RealEstateTabs.vue';
import AssignmentModal from '../modals/AssignmentModal.vue';
import ScheduleModal from '../modals/ScheduleModal.vue';
import AgentLeaveForm from '../modals/AgentLeaveForm.vue';
import AppointmentEditModal from '../modals/AppointmentEditModal.vue';
import RescheduleModal from '../modals/RescheduleModal.vue';
import { isProjectDeleted } from '../helpers';
import CalendarLeftPanel from './CalendarLeftPanel.vue';
import DayPanel from './DayPanel.vue';
import CreateChooser from './CreateChooser.vue';
import { transformToEvents } from './transform';
import { loadAgentColors, getAgentColor } from './agentColorMap';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import {
  getAssignment,
  deleteAssignment,
} from '../../../../api/realEstate/assignments';
import { getSchedule } from '../../../../api/realEstate/schedules';
import { listLeave, deleteLeave } from '../../../../api/realEstate/agentLeave';
import { getAppointment } from '../../../../api/realEstate/appointments';

// Views this page toggles between in its own header control, replacing
// FullCalendar's default view buttons (see calendarOptions' headerToolbar).
const VIEW_MONTH = 'dayGridMonth';
const VIEW_WEEK = 'timeGridWeek';

// Right-click delete is only offered for these — a schedule/operating-hours
// day can't be deleted without deleting the whole schedule/project hours.
const DELETABLE_SOURCE_TYPES = ['specific_assignment', 'unavailability'];

function pad(n) {
  return String(n).padStart(2, '0');
}

// Formats a FullCalendar callback `Date` (always local midnight, no
// timezone info) as `YYYY-MM-DD` without going through `toISOString`,
// which would shift the date for any positive UTC offset.
function toIsoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

export default {
  name: 'AgentCalendar',
  components: {
    PageHeader,
    LoadingState,
    DeleteModal,
    BaseModal,
    RealEstateTabs,
    FullCalendar,
    CalendarLeftPanel,
    DayPanel,
    CreateChooser,
    AssignmentModal,
    ScheduleModal,
    AgentLeaveForm,
    AppointmentEditModal,
    RescheduleModal,
  },
  data() {
    return {
      viewMonth: VIEW_MONTH,
      viewWeek: VIEW_WEEK,
      currentView: VIEW_MONTH,
      agentColorMap: {},
      lastRangeKey: null,

      activePanel: 'CalendarLeftPanel',
      activePanelProps: {},

      contextMenu: { visible: false, x: 0, y: 0, props: null },
      chooser: null, // { startDate, endDate } — inclusive YYYY-MM-DD
      leavePopover: null, // { ...extendedProps, reason }
      hoursPopover: null, // extendedProps
      bookingChoice: null, // full appointment
      deleteTarget: null, // { kind: 'assignment' | 'leave', id, agentId, label }
      isDeleting: false,
      isLoadingDetail: false,

      editingAssignment: null,
      editingAssignmentProject: null,
      editingSchedule: null,
      editingScheduleProject: null,
      editingAppointment: null,
      reschedulingAppointment: null,

      creatingAssignment: false,
      chooserPreset: null, // { agentId, startDate, endDate } | { agentId, date }
      creatingLeaveAgent: null, // { agent_id, name }
    };
  },
  computed: {
    ...mapGetters('realEstateCalendar', {
      calendarData: 'getCalendarData',
      filters: 'getFilters',
      isLoading: 'getIsLoading',
      range: 'getRange',
    }),
    ...mapGetters('realEstateAgents', { agents: 'getAgents' }),

    // Only the two filters the server actually accepts — a keyword change
    // alone is applied purely client-side in transformToEvents, so it must
    // not trigger a refetch.
    serverFilterKey() {
      return `${this.filters.agentId || ''}|${this.filters.propertyId || ''}`;
    },

    calendarEvents() {
      const labels = {
        viewing: this.$t('REAL_ESTATE.CALENDAR.EVENT_VIEWING'),
        key_handover: this.$t('REAL_ESTATE.CALENDAR.EVENT_KEY_HANDOVER'),
        onLeave: this.$t('REAL_ESTATE.CALENDAR.EVENT_ON_LEAVE'),
      };
      return transformToEvents(this.calendarData, this.filters, labels).map(
        evt => this.withColor(evt)
      );
    },

    // Dates the currently-filtered project is closed on — greying out a
    // day only makes sense once a single project is selected; with several
    // projects in view (or none picked) "closed" isn't a single answer for
    // the whole cell.
    closedDates() {
      const set = new Set();
      if (!this.filters.propertyId) return set;
      (this.calendarData.agents || []).forEach(agent => {
        agent.days.forEach(day => {
          if (
            day.property_id === this.filters.propertyId &&
            day.layer === 'property_closed'
          ) {
            set.add(day.date);
          }
        });
      });
      return set;
    },

    deleteName() {
      return this.deleteTarget ? this.deleteTarget.label : '';
    },

    bookingChoiceSubtitle() {
      if (!this.bookingChoice) return '';
      const { property, customer } = this.bookingChoice;
      return customer ? `${property.name} · ${customer.name}` : property.name;
    },

    // Mirrors PropertyAppointments.vue's APPT_ACTIONS_BY_STATUS — reschedule
    // isn't offered once a booking is completed (cancelled bookings never
    // reach the calendar at all, per §A11). Edit stays available regardless
    // of status there too, so it isn't gated here either.
    canRescheduleBookingChoice() {
      return (
        !!this.bookingChoice &&
        !isProjectDeleted(this.bookingChoice) &&
        ['requested', 'confirmed', 'rescheduled'].includes(
          this.bookingChoice.status
        )
      );
    },

    calendarOptions() {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: this.currentView,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: '',
        },
        events: this.calendarEvents,
        // Event titles already carry their times ("… 09:00–17:00"), so
        // FullCalendar's own "9a" label is off, and month view draws timed
        // events as solid agent-coloured bars (like the Staff Calendar)
        // instead of its default dot + text list item.
        // Set at the top level: a per-view `views.dayGridMonth` override
        // didn't reach the rendered month grid. Week view already draws
        // timed events as blocks, so this only changes month view.
        displayEventTime: false,
        eventDisplay: 'block',
        dayMaxEvents: 3,
        selectable: true,
        dayCellClassNames: arg =>
          this.closedDates.has(toIsoDate(arg.date)) ? ['re-cal-closed'] : [],
        datesSet: info => {
          const rangeKey = `${info.startStr}_${info.endStr}`;
          if (this.lastRangeKey === rangeKey) return;
          this.lastRangeKey = rangeKey;
          this.handleDateRangeChange(info.start, info.end);
        },
        // Hand the chooser plain inclusive YYYY-MM-DD dates. A month-view
        // selection is all-day with an exclusive `end`; a week-view one is
        // timed (`startStr` carries a time + offset), so its last day is
        // whichever day the selection's final moment falls on.
        select: info => {
          const lastDay = info.allDay
            ? this.minusOneDay(info.end)
            : toIsoDate(new Date(info.end.getTime() - 1));
          this.chooser = { startDate: toIsoDate(info.start), endDate: lastDay };
        },
        moreLinkClick: info => {
          this.openDayPanel(toIsoDate(info.date));
          return 'none';
        },
        eventClick: info => {
          this.routeEventClick(info.event.extendedProps);
        },
        eventDidMount: info => {
          info.el.addEventListener('contextmenu', e => {
            e.preventDefault();
            const { extendedProps } = info.event;
            if (!DELETABLE_SOURCE_TYPES.includes(extendedProps.source_type)) {
              return;
            }
            this.contextMenu = {
              visible: true,
              x: e.clientX,
              y: e.clientY,
              props: extendedProps,
            };
          });
        },
      };
    },
  },
  watch: {
    serverFilterKey() {
      this.refetchCurrentRange();
    },
  },
  mounted() {
    this.agentColorMap = loadAgentColors();
    this.$root.$on('reAgentColorUpdated', this.reloadColors);
    if (!this.agents.length) this.fetchAgents();

    document.addEventListener('click', this.closeContextMenu);
    document.addEventListener('scroll', this.closeContextMenu, true);
  },
  beforeDestroy() {
    this.$root.$off('reAgentColorUpdated', this.reloadColors);
    document.removeEventListener('click', this.closeContextMenu);
    document.removeEventListener('scroll', this.closeContextMenu, true);
  },
  methods: {
    ...mapActions('realEstateCalendar', ['fetchCalendar']),
    ...mapActions('realEstateAgents', ['fetchAgents']),
    ...mapActions('realEstate', ['fetchProject']),

    reloadColors() {
      this.agentColorMap = loadAgentColors();
    },

    withColor(evt) {
      const color = getAgentColor(
        this.agentColorMap,
        evt.extendedProps.agent_id
      );
      // Bookings get a light tint of the agent's colour (hex + 20% alpha)
      // with a solid left edge, so they stand apart from the same agent's
      // solid working-hours bars. No textColor here: the text colour comes
      // from --fc-event-text-color in the styles below, which swaps for
      // dark mode.
      if (evt.extendedProps.kind === 'booking') {
        return { ...evt, backgroundColor: `${color}33`, borderColor: color };
      }
      return {
        ...evt,
        backgroundColor: color,
        borderColor: color,
        textColor: getContrastingTextColor(color),
      };
    },

    minusOneDay(date) {
      const d = new Date(date);
      d.setDate(d.getDate() - 1);
      return toIsoDate(d);
    },

    async handleDateRangeChange(startDate, endDate) {
      const from = toIsoDate(startDate);
      // FullCalendar's `end` is exclusive; the BE's `to` is inclusive.
      const to = this.minusOneDay(endDate);
      try {
        await this.fetchCalendar({ from, to });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch agent calendar:', err);
      }
    },

    async refetchCurrentRange() {
      const { from, to } = this.range;
      if (!from || !to) return;
      try {
        await this.fetchCalendar({ from, to });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to refetch agent calendar:', err);
      }
    },

    setView(viewName) {
      this.currentView = viewName;
      const api = this.$refs.calendar && this.$refs.calendar.getApi();
      if (api) api.changeView(viewName);
    },

    // -- day panel ("+N more") --
    openDayPanel(date) {
      this.activePanel = 'DayPanel';
      this.activePanelProps = { date };
    },
    closeDayPanel() {
      this.activePanel = 'CalendarLeftPanel';
      this.activePanelProps = {};
    },

    // -- click routing --
    async routeEventClick(props) {
      this.closeContextMenu();
      if (props.kind === 'booking') {
        await this.openBookingChoice(props.appointment_id);
      } else if (props.source_type === 'unavailability') {
        await this.openLeavePopover(props);
      } else if (props.source_type === 'operating_hours') {
        this.hoursPopover = props;
      } else if (props.source_type === 'specific_assignment') {
        await this.openAssignmentEdit(props);
      } else if (props.source_type === 'schedule') {
        await this.openScheduleEdit(props);
      }
    },

    async openBookingChoice(appointmentId) {
      this.isLoadingDetail = true;
      try {
        const { data } = await getAppointment(appointmentId);
        this.bookingChoice = data;
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.CALENDAR.BOOKING_LOAD_ERROR')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    openBookingEdit() {
      this.editingAppointment = this.bookingChoice;
      this.bookingChoice = null;
    },
    openBookingReschedule() {
      this.reschedulingAppointment = this.bookingChoice;
      this.bookingChoice = null;
    },
    handleAppointmentModalClosed() {
      this.editingAppointment = null;
      this.reschedulingAppointment = null;
      this.refetchCurrentRange();
    },

    async openLeavePopover(props) {
      this.leavePopover = { ...props, reason: null };
      try {
        const { data } = await listLeave(props.agent_id, {
          from_date: props.date,
          to_date: props.date,
        });
        const match = data.results.find(r => r.id === props.source_id);
        if (match)
          this.leavePopover = { ...this.leavePopover, reason: match.reason };
      } catch (err) {
        // Best-effort only — the popover still works (minus the reason
        // line) if this lookup fails.
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    confirmDeleteLeaveFromPopover() {
      this.deleteTarget = this.deleteTargetFor(this.leavePopover);
      this.leavePopover = null;
    },

    // Shared by the right-click menu, the day panel and the leave popover —
    // builds what DeleteModal confirms, e.g. "Jane Doe's leave on 9 Oct
    // 2026". Only one-off assignments and leave are ever deletable here.
    deleteTargetFor(props) {
      if (props.source_type === 'specific_assignment') {
        return {
          kind: 'assignment',
          id: props.source_id,
          label: this.$t('REAL_ESTATE.CALENDAR.DELETE_ASSIGNMENT_LABEL', {
            name: props.agent_name,
            project: props.property_name,
          }),
        };
      }
      if (props.source_type === 'unavailability') {
        return {
          kind: 'leave',
          id: props.source_id,
          agentId: props.agent_id,
          label: this.$t('REAL_ESTATE.CALENDAR.DELETE_LEAVE_LABEL', {
            name: props.agent_name,
            date: format(parseISO(props.date), 'd MMM yyyy'),
          }),
        };
      }
      return null;
    },

    async openAssignmentEdit(props) {
      this.isLoadingDetail = true;
      try {
        const [{ data: assignment }, project] = await Promise.all([
          getAssignment(props.source_id),
          this.fetchProject(props.property_id),
        ]);
        this.editingAssignment = assignment;
        this.editingAssignmentProject = project;
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.CALENDAR.ERROR_LOAD'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    handleAssignmentModalClosed() {
      this.editingAssignment = null;
      this.editingAssignmentProject = null;
      this.creatingAssignment = false;
      this.chooserPreset = null;
    },
    handleAssignmentSaved() {
      this.handleAssignmentModalClosed();
      this.refetchCurrentRange();
    },

    async openScheduleEdit(props) {
      this.isLoadingDetail = true;
      try {
        const [{ data: schedule }, project] = await Promise.all([
          getSchedule(props.source_id),
          this.fetchProject(props.property_id),
        ]);
        this.editingSchedule = schedule;
        this.editingScheduleProject = project;
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.CALENDAR.ERROR_LOAD'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    handleScheduleModalClosed() {
      this.editingSchedule = null;
      this.editingScheduleProject = null;
    },
    handleScheduleSaved() {
      this.handleScheduleModalClosed();
      this.refetchCurrentRange();
    },

    // -- context menu (right-click delete) --
    closeContextMenu() {
      this.contextMenu = { visible: false, x: 0, y: 0, props: null };
    },
    openContextDelete() {
      this.deleteTarget = this.deleteTargetFor(this.contextMenu.props);
      this.closeContextMenu();
    },

    // -- delete (shared by the context menu and the leave popover) --
    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.isDeleting = true;
      try {
        if (this.deleteTarget.kind === 'assignment') {
          await deleteAssignment(this.deleteTarget.id);
          useAlert(this.$t('REAL_ESTATE.CALENDAR.DELETE_ASSIGNMENT_SUCCESS'));
        } else {
          await deleteLeave(this.deleteTarget.agentId, this.deleteTarget.id);
          useAlert(this.$t('REAL_ESTATE.CALENDAR.DELETE_LEAVE_SUCCESS'));
        }
        this.deleteTarget = null;
        await this.refetchCurrentRange();
      } catch (err) {
        const fallback =
          this.deleteTarget.kind === 'assignment'
            ? 'REAL_ESTATE.CALENDAR.DELETE_ASSIGNMENT_ERROR'
            : 'REAL_ESTATE.CALENDAR.DELETE_LEAVE_ERROR';
        useAlert(apiErrorMessage(this, err, fallback));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },

    // -- day panel delegation --
    handleDayPanelSelect(props) {
      this.closeDayPanel();
      this.routeEventClick(props);
    },
    handleDayPanelDelete(props) {
      this.deleteTarget = this.deleteTargetFor(props);
    },

    // -- create chooser (drag-select) --
    closeChooser() {
      this.chooser = null;
    },
    handleChooseAssignment({ agentId, startDate, endDate }) {
      this.chooser = null;
      this.chooserPreset = { agentId, startDate, endDate };
      this.creatingAssignment = true;
    },
    handleChooseLeave({ agentId, date }) {
      this.chooser = null;
      const agent = this.agents.find(a => a.agent_id === agentId);
      this.creatingLeaveAgent = agent || { agent_id: agentId, name: '' };
      this.chooserPreset = { date };
    },
    closeLeaveCreate() {
      this.creatingLeaveAgent = null;
      this.chooserPreset = null;
    },
    handleLeaveAdded() {
      this.closeLeaveCreate();
      this.refetchCurrentRange();
    },
  },
};
</script>

<template>
  <div class="re-agent-calendar p-8 h-full w-full flex flex-col overflow-auto">
    <PageHeader
      :title="$t('REAL_ESTATE.CALENDAR.HEADER_TITLE')"
      icon="calendar"
      :description="$t('REAL_ESTATE.CALENDAR.HEADER_DESCRIPTION')"
    >
      <template #actions>
        <div class="flex bg-slate-100 dark:bg-slate-700 rounded-md p-1">
          <button
            type="button"
            class="px-3 py-1 rounded-md text-sm transition-colors"
            :class="
              currentView === viewMonth
                ? 'bg-white dark:bg-slate-600 shadow text-slate-900 dark:text-slate-100'
                : 'text-slate-500 dark:text-slate-300'
            "
            @click="setView(viewMonth)"
          >
            {{ $t('REAL_ESTATE.CALENDAR.VIEW_MONTH') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 rounded-md text-sm transition-colors"
            :class="
              currentView === viewWeek
                ? 'bg-white dark:bg-slate-600 shadow text-slate-900 dark:text-slate-100'
                : 'text-slate-500 dark:text-slate-300'
            "
            @click="setView(viewWeek)"
          >
            {{ $t('REAL_ESTATE.CALENDAR.VIEW_WEEK') }}
          </button>
        </div>
      </template>
    </PageHeader>

    <RealEstateTabs class="mb-6" />

    <!-- Drag-select chooser -->
    <CreateChooser
      v-if="chooser"
      :start-date="chooser.startDate"
      :end-date="chooser.endDate"
      :agents="agents"
      :default-agent-id="filters.agentId || ''"
      @chooseAssignment="handleChooseAssignment"
      @chooseLeave="handleChooseLeave"
      @close="closeChooser"
    />

    <!-- Booking click -> Edit / Reschedule -->
    <div
      v-if="bookingChoice"
      class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-xs space-y-2"
      >
        <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          {{ bookingChoiceSubtitle }}
        </p>
        <woot-button class="w-full" @click="openBookingEdit">
          {{ $t('REAL_ESTATE.CALENDAR.BOOKING_EDIT') }}
        </woot-button>
        <woot-button
          v-if="canRescheduleBookingChoice"
          class="w-full"
          color-scheme="secondary"
          @click="openBookingReschedule"
        >
          {{ $t('REAL_ESTATE.CALENDAR.BOOKING_RESCHEDULE') }}
        </woot-button>
        <woot-button
          class="w-full"
          variant="clear"
          @click="bookingChoice = null"
        >
          {{ $t('REAL_ESTATE.CALENDAR.POPOVER_CLOSE') }}
        </woot-button>
      </div>
    </div>

    <AppointmentEditModal
      v-if="editingAppointment"
      :appointment="editingAppointment"
      @close="handleAppointmentModalClosed"
    />
    <RescheduleModal
      v-if="reschedulingAppointment"
      :appointment="reschedulingAppointment"
      @close="handleAppointmentModalClosed"
    />

    <!-- Leave details popover -->
    <div
      v-if="leavePopover"
      class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-xs space-y-2"
      >
        <h2 class="text-lg font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.CALENDAR.LEAVE_POPOVER_TITLE') }}
        </h2>
        <p class="text-sm text-slate-700 dark:text-slate-200">
          {{ leavePopover.agent_name }} · {{ leavePopover.date }}
        </p>
        <p v-if="leavePopover.reason" class="text-sm text-slate-400">
          {{ leavePopover.reason }}
        </p>
        <div class="flex justify-end gap-2 pt-2">
          <woot-button variant="clear" @click="leavePopover = null">
            {{ $t('REAL_ESTATE.CALENDAR.POPOVER_CLOSE') }}
          </woot-button>
          <woot-button
            color-scheme="alert"
            @click="confirmDeleteLeaveFromPopover"
          >
            {{ $t('REAL_ESTATE.CALENDAR.LEAVE_POPOVER_DELETE') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Operating-hours info popover -->
    <div
      v-if="hoursPopover"
      class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
    >
      <div
        class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-xs space-y-2"
      >
        <h2 class="text-lg font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.CALENDAR.HOURS_POPOVER_TITLE') }}
        </h2>
        <p class="text-sm text-slate-700 dark:text-slate-200">
          {{ hoursPopover.agent_name }} · {{ hoursPopover.property_name }}
        </p>
        <div class="flex justify-between items-center pt-2">
          <router-link
            :to="{
              name: 'real_estate_project',
              params: { projectId: hoursPopover.property_id },
            }"
            class="text-sm text-woot-500 hover:underline font-medium"
            @click.native="hoursPopover = null"
          >
            {{ $t('REAL_ESTATE.CALENDAR.HOURS_POPOVER_LINK') }}
          </router-link>
          <woot-button variant="clear" @click="hoursPopover = null">
            {{ $t('REAL_ESTATE.CALENDAR.POPOVER_CLOSE') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Assignment / schedule edit -->
    <AssignmentModal
      v-if="editingAssignment"
      :project="editingAssignmentProject"
      :assignment="editingAssignment"
      @close="handleAssignmentModalClosed"
      @saved="handleAssignmentSaved"
    />
    <ScheduleModal
      v-if="editingSchedule"
      :project="editingScheduleProject"
      :schedule="editingSchedule"
      @close="handleScheduleModalClosed"
      @saved="handleScheduleSaved"
    />

    <!-- Create from the chooser -->
    <AssignmentModal
      v-if="creatingAssignment"
      :assignment="null"
      :preset-agent-id="chooserPreset.agentId"
      :preset-start-date="chooserPreset.startDate"
      :preset-end-date="chooserPreset.endDate"
      @close="handleAssignmentModalClosed"
      @saved="handleAssignmentSaved"
    />
    <BaseModal v-if="creatingLeaveAgent" @close="closeLeaveCreate">
      <template #title>
        {{
          creatingLeaveAgent.name
            ? $t('REAL_ESTATE.CALENDAR.LEAVE_MODAL_TITLE', {
                name: creatingLeaveAgent.name,
              })
            : $t('REAL_ESTATE.CALENDAR.CHOOSER_LEAVE')
        }}
      </template>
      <AgentLeaveForm
        :agent="creatingLeaveAgent"
        :preset-date="chooserPreset.date"
        @added="handleLeaveAdded"
      />
    </BaseModal>

    <!-- Right-click delete -->
    <div
      v-if="contextMenu.visible"
      class="fixed bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-md rounded border border-slate-200 dark:border-slate-700 p-2 z-50"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <woot-button
        icon="delete"
        color-scheme="alert"
        class="w-full"
        @click="openContextDelete"
      >
        {{ $t('REAL_ESTATE.CALENDAR.CONTEXT_DELETE') }}
      </woot-button>
    </div>

    <DeleteModal
      v-if="deleteTarget"
      :delete-name="deleteName"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="deleteTarget = null"
    />

    <div class="relative flex gap-4 w-full flex-1 min-h-0">
      <component
        :is="activePanel"
        v-bind="activePanelProps"
        @close="closeDayPanel"
        @selectEvent="handleDayPanelSelect"
        @deleteEvent="handleDayPanelDelete"
      />

      <div
        v-if="isLoading || isLoadingDetail"
        class="absolute inset-0 z-40 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm"
      >
        <LoadingState :message="$t('REAL_ESTATE.CALENDAR.LOADING')" />
      </div>

      <FullCalendar
        ref="calendar"
        class="flex-1 min-w-0"
        :options="calendarOptions"
      />
    </div>
  </div>
</template>

<style>
/* Unscoped (not :deep) so these reach FullCalendar's own markup and the
   body.dark ancestor toggle — see CLAUDE.md's CSS gotchas note. Nested
   under .re-agent-calendar, the page's one unique root class, instead of
   !important. */
.re-agent-calendar .re-cal-closed {
  background-color: rgba(100, 116, 139, 0.08);
}

.re-agent-calendar .re-cal-working-hours {
  cursor: pointer;
}

/* Week view: working hours are the backdrop, bookings sit on top of them —
   fade the hours blocks so a booking inside them stands out. */
.re-agent-calendar .fc-timegrid-event.re-cal-working-hours {
  opacity: 0.45;
}

.re-agent-calendar .fc-timegrid-event.re-cal-booking {
  z-index: 3;
}

.re-agent-calendar .re-cal-leave-partial {
  font-style: italic;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.35) 0,
    rgba(255, 255, 255, 0.35) 4px,
    transparent 4px,
    transparent 8px
  );
}

.re-agent-calendar .re-cal-leave-allday {
  font-style: italic;
  cursor: pointer;
}

.re-agent-calendar .re-cal-booking {
  cursor: pointer;
}

.re-agent-calendar .fc-event.re-cal-booking {
  border-left-width: 4px;
  font-weight: 600;
  --fc-event-text-color: #1e293b;
}

body.dark .re-agent-calendar .fc-event.re-cal-booking {
  --fc-event-text-color: #e2e8f0;
}

body.dark .re-agent-calendar .re-cal-closed {
  background-color: rgba(148, 163, 184, 0.12);
}

/* FullCalendar's own theme relies on CSS custom properties that don't
   swap with the dashboard's dark mode on their own; the Organization
   Staff Calendar (organization/staffCalendar/Index.vue) has no dark-mode
   overrides of its own to copy, so these are new, kept minimal. */
body.dark .re-agent-calendar .fc {
  --fc-border-color: #334155;
  --fc-neutral-bg-color: #1e293b;
  --fc-page-bg-color: transparent;
  --fc-list-event-hover-bg-color: #334155;
  --fc-today-bg-color: rgba(59, 130, 246, 0.08);
}

body.dark .re-agent-calendar .fc-col-header-cell,
body.dark .re-agent-calendar .fc-daygrid-day-number,
body.dark .re-agent-calendar .fc-timegrid-slot-label,
body.dark .re-agent-calendar .fc-timegrid-axis,
body.dark .re-agent-calendar .fc-toolbar-title,
body.dark .re-agent-calendar .fc-daygrid-more-link {
  color: #cbd5e1;
}

body.dark .re-agent-calendar .fc-button-primary {
  background-color: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

body.dark .re-agent-calendar .fc-button-primary:disabled {
  opacity: 0.5;
}
</style>
